import { motion } from "framer-motion";
import { Star, Quote, BadgeCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/animations/AnimatedSection";
import { useReviews, formatReviewDate } from "@/hooks/useReviews";
import { Button } from "@/components/ui/button";

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? "fill-amber-400 text-amber-400" : "text-muted"
        }`}
      />
    ))}
  </div>
);

export function TestimonialsSection() {
  const { reviews, stats } = useReviews();

  // Take only the first 3 reviews for the homepage
  const displayReviews = reviews.slice(0, 3);

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-background">
      <div className="container-calim">
        <AnimatedSection className="text-center mb-10 md:mb-12">
          <p className="text-subhead mb-3 md:mb-4">Testimonials</p>
          <h2 className="text-headline mb-4">
            What Our Customers Say
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Discover verified reviews from our CALMISTYLE community
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {displayReviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-secondary/30 rounded-2xl p-5 md:p-6 relative"
            >
              {/* Quote icon */}
              <Quote className="absolute top-4 right-4 h-6 md:h-8 w-6 md:w-8 text-primary/10" />

              {/* Header */}
              <div className="flex items-start gap-3 md:gap-4 mb-4">
                <div className="w-10 md:w-12 h-10 md:h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-display text-lg md:text-xl">
                  {review.reviewerName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm md:text-base">{review.reviewerName}</span>
                    {review.verified && (
                      <BadgeCheck className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {formatReviewDate(review.createdAt)}
                  </p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center justify-between mb-3">
                <StarRating rating={review.rating} />
              </div>

              {/* Review image - lazy loaded */}
              {review.pictureUrls.length > 0 && (
                <div className="mb-3">
                  <img 
                    src={review.pictureUrls[0]} 
                    alt="Customer photo"
                    loading="lazy"
                    decoding="async"
                    width={300}
                    height={128}
                    className="w-full h-28 md:h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Review text */}
              <p className="text-muted-foreground text-sm leading-relaxed line-clamp-4">
                "{review.body}"
              </p>

              {/* Verified badge */}
              {review.verified && (
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-green-600">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  <span>Verified Purchase</span>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Trust indicators */}
        <AnimatedSection delay={0.3} className="mt-10 md:mt-12 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-6 px-4 md:px-6 py-3 bg-secondary/50 rounded-full mb-6">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-medium">{stats.averageRating}/5</span>
            </div>
            <div className="w-px h-6 bg-border hidden sm:block" />
            <span className="text-muted-foreground text-sm">
              Based on {stats.totalReviews} verified reviews
            </span>
          </div>
          
          <div>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/avis">
                See all reviews
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
